import { templateCategoryLabel, type WabaTemplate } from "@school-sales-ai/shared";
import { TemplateStatusBadge } from "./TemplateStatusBadge";
import { qualityLabel } from "./template-status";

/** The row actions that hit the network, i.e. the ones that can be in flight. */
export type TemplateRowAction = "submit" | "archive" | "unarchive";

/** Which row is waiting on the server, and for what. */
export type TemplateRowBusy = { id: string; action: TemplateRowAction } | null;

function Spinner(): React.JSX.Element {
  return (
    <svg className="h-3 w-3 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
    </svg>
  );
}

/**
 * The day the school created the template here, not the day Meta approved it —
 * `submittedAt` / `lastStatusAt` are the Meta-side dates and stay off the table.
 * Numeric and 2-digit to match the lead table's date column: this one is scanned
 * down a list, not read as prose.
 */
function formatCreatedAt(iso: string): string {
  return new Date(iso).toLocaleDateString("es-MX", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

/**
 * Everything on this table is read-only except the actions column: `status`,
 * `category` and `qualityScore` are Meta's, written only by the webhook and the
 * reconcile sweep.
 */
export function TemplatesTable({
  templates,
  isSuperAdmin,
  canEdit,
  busy,
  onEdit,
  onSubmit,
  onArchive,
  onUnarchive,
  onDeleteInMeta,
}: {
  templates: WabaTemplate[];
  isSuperAdmin: boolean;
  canEdit: boolean;
  busy: TemplateRowBusy;
  onEdit: (t: WabaTemplate) => void;
  onSubmit: (t: WabaTemplate) => void;
  onArchive: (t: WabaTemplate) => void;
  onUnarchive: (t: WabaTemplate) => void;
  onDeleteInMeta: (t: WabaTemplate) => void;
}): React.JSX.Element {
  if (templates.length === 0) {
    return (
      <p className="rounded-lg border border-dashed border-ink-200 p-8 text-center text-sm text-ink-500">
        Todavía no hay plantillas. Crea una y envíala a Meta para poder escribir
        fuera de la ventana de 24 horas.
      </p>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[58rem] text-sm">
        <thead>
          <tr className="border-b border-ink-200 text-left text-xs uppercase text-ink-500">
            <th className="py-2 pr-4">Nombre</th>
            <th className="py-2 pr-4">Estado</th>
            <th className="py-2 pr-4">Categoría</th>
            <th className="py-2 pr-4">Idioma</th>
            <th className="py-2 pr-4">Creada</th>
            <th className="py-2 pr-4">Mensaje</th>
            <th className="py-2 pr-4" />
          </tr>
        </thead>
        <tbody>
          {templates.map((t) => (
            <tr
              key={t.id}
              className={`border-b border-ink-100 align-top ${
                t.archivedAt ? "opacity-60" : ""
              }`}
            >
              <td className="py-3 pr-4">
                <span className="font-medium text-ink-900">{t.name}</span>
                {t.archivedAt ? (
                  <span className="ml-2 text-xs text-ink-500">(archivada)</span>
                ) : null}
                {t.qualityScore && t.qualityScore !== "GREEN" ? (
                  <p className="text-xs text-orange-700">{qualityLabel(t.qualityScore)}</p>
                ) : null}
              </td>
              <td className="py-3 pr-4">
                <TemplateStatusBadge status={t.status} />
                {t.rejectedReason ? (
                  <p className="mt-1 max-w-xs text-xs text-red-700">
                    {t.rejectionRecommendation ?? t.rejectedReason}
                  </p>
                ) : null}
              </td>
              <td className="py-3 pr-4">
                <span>{templateCategoryLabel(t.category)}</span>
                {/* The gap between what was asked for and what Meta decided is
                    the bill, so it is stated rather than left to be inferred. */}
                {t.category !== t.requestedCategory ? (
                  <p className="mt-0.5 max-w-[14rem] text-xs text-amber-700">
                    Meta la reclasificó (pediste {templateCategoryLabel(t.requestedCategory)}).
                    Se cobra como {templateCategoryLabel(t.category)}.
                  </p>
                ) : null}
              </td>
              <td className="py-3 pr-4 text-ink-600">{t.language}</td>
              <td className="whitespace-nowrap py-3 pr-4 text-ink-600">
                {formatCreatedAt(t.createdAt)}
              </td>
              <td className="max-w-sm py-3 pr-4 text-ink-700">
                <p className="line-clamp-2">{t.body}</p>
                {t.editBudget && !t.editBudget.canEditNow ? (
                  <p className="mt-1 text-xs text-ink-500">
                    Ediciones: {t.editBudget.usedIn30Days}/{t.editBudget.maxIn30Days} en
                    30 días · Meta permite una cada 24 h
                  </p>
                ) : null}
              </td>
              <td className="py-3 pr-4">
                {canEdit ? (
                  <RowActions
                    template={t}
                    busy={busy}
                    isSuperAdmin={isSuperAdmin}
                    onEdit={onEdit}
                    onSubmit={onSubmit}
                    onArchive={onArchive}
                    onUnarchive={onUnarchive}
                    onDeleteInMeta={onDeleteInMeta}
                  />
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * The actions column.
 *
 * Everything here is disabled while that row has a request in flight. "Enviar a
 * validar" is the reason: it is the only action that waits on a Graph round trip
 * (seconds, not milliseconds), and with no in-flight state it read as a dead
 * button — so the natural next move was to click it again, and two concurrent
 * submits would create the template in Meta twice. The backend's `metaTemplateId`
 * guard only rejects the second call once the first has already returned.
 */
function RowActions({
  template: t,
  busy,
  isSuperAdmin,
  onEdit,
  onSubmit,
  onArchive,
  onUnarchive,
  onDeleteInMeta,
}: {
  template: WabaTemplate;
  busy: TemplateRowBusy;
  isSuperAdmin: boolean;
  onEdit: (t: WabaTemplate) => void;
  onSubmit: (t: WabaTemplate) => void;
  onArchive: (t: WabaTemplate) => void;
  onUnarchive: (t: WabaTemplate) => void;
  onDeleteInMeta: (t: WabaTemplate) => void;
}): React.JSX.Element {
  const pending = busy?.id === t.id ? busy.action : null;
  const disabled = pending !== null;
  const base = "text-xs underline disabled:no-underline disabled:opacity-50";

  return (
    <div className="flex flex-wrap items-center justify-end gap-2">
      {t.archivedAt ? (
        <button
          type="button"
          onClick={() => onUnarchive(t)}
          disabled={disabled}
          className={`${base} text-ink-700`}
        >
          {pending === "unarchive" ? (
            <span className="flex items-center gap-1.5">
              <Spinner />
              Desarchivando…
            </span>
          ) : (
            "Desarchivar"
          )}
        </button>
      ) : (
        <>
          <button
            type="button"
            onClick={() => onEdit(t)}
            disabled={disabled}
            className={`${base} text-ink-700`}
          >
            Editar
          </button>
          {!t.metaTemplateId ? (
            <button
              type="button"
              onClick={() => onSubmit(t)}
              disabled={disabled}
              className={`${base} font-medium text-ink-900`}
            >
              {pending === "submit" ? (
                <span className="flex items-center gap-1.5">
                  <Spinner />
                  Enviando a Meta…
                </span>
              ) : (
                "Enviar a validar"
              )}
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => onArchive(t)}
            disabled={disabled}
            className={`${base} text-ink-700`}
          >
            {pending === "archive" ? (
              <span className="flex items-center gap-1.5">
                <Spinner />
                Archivando…
              </span>
            ) : (
              "Archivar"
            )}
          </button>
        </>
      )}
      {/* Super-admin only: deleting locks the name in Meta for 30
          days, which is not what "quitar de mi lista" should do. */}
      {isSuperAdmin && t.metaTemplateId && t.status !== "DELETED" ? (
        <button
          type="button"
          onClick={() => onDeleteInMeta(t)}
          disabled={disabled}
          className={`${base} text-red-700`}
        >
          Eliminar en Meta
        </button>
      ) : null}
    </div>
  );
}
